import Loading from '../Loading/Loading';
import { COLORS } from '../../config/designSystem/Colors';
import { DashboardLoadingOverlay, HealthDashboardError, NewScanButton, NoFaceScanCard } from './shared';
import { IntelliProveMessage, useHealthDashboard } from '../../hooks/content/faceScan/useHealthDashboard';
import { StyleSheet, View } from 'react-native';
import { useEffect, useState } from 'react';

const iframeStyle = {
  border: 'none',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
};

export const HealthDashboard = () => {
  const {
    hasFaceScanResults,
    dashboardUrl,
    loading,
    error,
    labels,
    retry,
    handleIntelliProveMessage,
    goToFaceScan,
  } = useHealthDashboard();
  const [iframeLoading, setIframeLoading] = useState(true);

  useEffect(() => {
    if (!hasFaceScanResults) return;

    const handleMessage = (e: MessageEvent<IntelliProveMessage>) => {
      if (e.data?.stage) {
        handleIntelliProveMessage(e.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hasFaceScanResults, handleIntelliProveMessage]);

  if (hasFaceScanResults === null) {
    return <Loading />;
  }

  if (!hasFaceScanResults) {
    return (
      <View style={styles.container}>
        <NoFaceScanCard
          message={labels.health?.NO_FACE_SCAN_MESSAGE || 'Complete your first face scan to see your health dashboard'}
          buttonLabel={labels.health?.START_FACE_SCAN_BUTTON || 'Start Face Scan'}
          onPress={goToFaceScan}
        />
      </View>
    );
  }

  if (loading || (!dashboardUrl && !error)) {
    return <Loading />;
  }

  if (error) {
    return (
      <View style={styles.container}>
        <HealthDashboardError
          message={labels.settings?.HEALTH_DASHBOARD_ERROR || 'Could not load health dashboard. Please try again later.'}
          retryLabel={labels.settings?.RETRY || 'Retry'}
          onRetry={retry}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {dashboardUrl && (
        <iframe
          src={dashboardUrl}
          id="intelliprove-dashboard-iframe"
          allow="fullscreen"
          style={iframeStyle}
          onLoad={() => setIframeLoading(false)}
        />
      )}
      {iframeLoading && dashboardUrl && (
        <DashboardLoadingOverlay message={labels.settings?.LOADING || 'Loading...'} />
      )}
      {!iframeLoading && dashboardUrl && (
        <NewScanButton
          label={labels.health?.NEW_SCAN_BUTTON || 'New Scan'}
          onPress={goToFaceScan}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.surface.primary,
  },
});
